import {
  addDays,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import {
  SuperApiClaimOwnerCapacity,
  SuperApiClaimOwnerDayCapacity,
  SuperApiClaimOwnerWeekCapacity,
} from '@dispatch/shared/types';
import { getTimeWithOffset } from '@dispatch/shared';
import { trackEvent } from '@dispatch/web/telemetry';
import { escapeHtml } from '../utils/escapeHtml';

/**
 * Claim Owner capacity band for the Scheduler View resource row (ADO #104914).
 *
 * ONE MODEL, MANY SURFACES
 * `buildCapacityModel` does all of the arithmetic once; the band renderers —
 * and, later, the hover tooltip — consume the result. A second copy of the
 * `min(daily, weekly)` rule is exactly how two screens end up contradicting
 * each other, which the ticket classifies as a defect.
 *
 * SEVEN DAYS. Scheduler View shows one week at a time, so the band shows
 * exactly those seven days plus a weekly summary for each Monday-to-Sunday
 * week they touch. Because a week can extend past the window, each weekly line
 * also names the portion of its total already spent off screen — see
 * `usedEarlierThisWeek`.
 *
 * PAST DAYS ARE ZERO CAPACITY — an em dash and "off", like any zero-cap day.
 *
 * The figures are positioned on the time axis by pixel projection rather than
 * laid out as a seven-column grid: Scheduler View pins `minZoomLevel: 13`, so
 * the coarsest preset is still `hourAndDay` and a day is 256–1680px wide.
 * Nothing here is editable — the band is display only.
 */

/* -------------------------------------------------------------------------- */
/* Model                                                                      */
/* -------------------------------------------------------------------------- */

export interface CapacityWeek {
  /** `weekStart` date key — the week's identity. */
  key: string;
  start: Date;
  end: Date;
  /**
   * From `weeks[].cap`, which is authoritative for THIS week. The top-level
   * `weeklyCap` is documented as equal to it under the current data model, so
   * it is not a substitute — read the per-week figure.
   */
  cap: number;
  /** Full Monday–Sunday used, straight from the payload. */
  used: number;
  remaining: number;
  /** False when every day of the week is already past (AC 24). */
  hasFutureDays: boolean;
  /** True when the board's today falls inside this week. */
  containsToday: boolean;
  /**
   * The week's used total MINUS the part of it the band actually shows — the
   * residual, and never negative.
   *
   * Deliberately neutral in name. It is the arithmetic fact, valid for every
   * week; what that residual MEANS depends on where the week sits relative to
   * the window, which is `usedEarlierThisWeek`'s job.
   *
   * "Shown" is the week's own days inside the seven-day window, taking each
   * day's model `used` — so a past or unserved column, which displays an em
   * dash rather than figures, counts as not shown and its consumption lands
   * here where it can be named.
   */
  usedOutsideWindow: number;
  /**
   * `usedOutsideWindow`, but only when every day it could have come from
   * precedes the board's today — i.e. it is genuinely "used earlier this week"
   * (AC 29). Zero otherwise.
   *
   * This is the ONE field the weekly line and the hover tooltip both read, so
   * neither surface re-derives the rule and they cannot disagree.
   */
  usedEarlierThisWeek: number;
}

export interface CapacityDay {
  date: Date;
  /** `YYYY-MM-DD` key, the same form the payload uses. */
  key: string;
  cap: number;
  used: number;
  /** `min(daily remaining, weekly remaining)` — the availability figure (AC 3). */
  remaining: number;
  isToday: boolean;
  /** Before today. Never carries figures, in any view range (AC 25). */
  isPast: boolean;
  /**
   * Zero cap or past — renders as an em dash plus "off" (AC 7, AC 24), which is
   * the UNAVAILABILITY treatment. Also set when `isMissing` is, but a missing
   * date is rendered differently; see below.
   */
  isOff: boolean;
  /**
   * No payload entry for this date. The contract requires one entry per
   * requested date, so this means the drawn axis has moved ahead of the payload
   * that fed it — the usual, transient case — or the producer is at fault.
   *
   * Rendered as a SUBDUED dash with no "off" label, distinct from an
   * unavailable day. AC 31: a missing date "is a data defect and is surfaced as
   * such — never rendered as a day the resource is unavailable", and the DTO
   * contract says the same. Checked before `isOff` in both renderers and in the
   * tooltip, because a missing day sets both.
   */
  isMissing: boolean;
  /** Availability is zero *only* because the week is exhausted (AC 4). */
  isWeeklyGated: boolean;
  week?: CapacityWeek;
}

export interface CapacityModel {
  days: CapacityDay[];
  weeks: CapacityWeek[];
  /** First weekly-gated day, so the reason can be stated on screen (AC 4). */
  gated: CapacityDay | null;
  timeZoneId: string | null;
  /**
   * Drawn columns that resolved to no figures because their date key found no
   * `days[]` entry (or no week). Provenance for the renderer's detection signal
   * only — nothing on screen reads it. A handful is legitimate; see
   * `reportCapacityMismatch`.
   */
  missingDayCount: number;
  /**
   * Distinct Mondays computed from the window for which `weeks[]` served no
   * entry. Same purpose: detection only.
   */
  unmatchedWeekCount: number;
}

/** Scheduler View shows one week; the band shows exactly those days. */
export const CAPACITY_WINDOW_DAYS = 7;

const DATE_KEY_FORMAT = 'yyyy-MM-dd';
const SHORT_DATE_FORMAT = 'MM/dd';
/** Below this the week's date range clips into the next bar, so it is dropped. */
const WEEK_RANGE_MIN_WIDTH_PX = 190;

/** Local `YYYY-MM-DD`. Deliberately not UTC — the axis is local wall-clock. */
function toDateKey(date: Date): string {
  return format(date, DATE_KEY_FORMAT);
}

/**
 * Parse a payload date key to a LOCAL midnight Date.
 *
 * The API computes day and week boundaries in the board's selected
 * `scheduleTimeZoneId` — the same calendar these columns are drawn in (AC 6, as
 * corrected 2026-08-26) — and sends plain calendar dates, so the front end must
 * not re-apply any offset: it treats the keys as calendar labels and keys every
 * lookup off them. `parseISO` on a date-only string yields local midnight,
 * unlike `new Date('2026-08-24')` which is UTC midnight and can land a day out.
 */
function parseDateKey(key: string): Date | null {
  if (!key) return null;
  const parsed = parseISO(key.slice(0, 10));
  return isValid(parsed) ? startOfDay(parsed) : null;
}

function mondayOf(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 });
}

/**
 * Build the capacity model for one resource over the seven days starting at
 * `rangeStart`.
 *
 * Returns `null` only when there is NO capacity block — the AC 8 signal. A
 * zero-filled object is not that signal, and neither is a block whose `days`
 * list is empty: an empty list means the window produced no days.
 *
 * `today` is injectable so the calendar-relative rules (past days, a fully
 * past week) are testable without freezing the clock.
 *
 * `timeZone` is the zone the board is showing, and it is what makes `today`
 * mean the same thing as the axis. The API converts every datetime in the
 * `schedules-new` response into the selected zone and serializes it naive, and
 * Bryntum is deliberately NOT given a `timeZone` for that reason, so the whole
 * board is that zone's wall clock. Reading "today" from the browser instead
 * mislabels a column past/not-past whenever the two are on different calendar
 * days — and a past column is stripped of its figures, so a legitimate day
 * would silently show none. Same conversion, same helper, as the hand-placed
 * current-time line in SchedulerCore. Omitted, it falls back to browser local,
 * which is only correct when the two zones agree.
 */
export function buildCapacityModel(
  capacity: SuperApiClaimOwnerCapacity | null | undefined,
  rangeStart: Date,
  today: Date = new Date(),
  timeZone?: string
): CapacityModel | null {
  if (!capacity) return null;

  const firstDay = startOfDay(rangeStart);
  // getTimeWithOffset returns a "fake" Date whose LOCAL fields read as the
  // target zone's wall clock, which is exactly what the date-key comparisons
  // below need — every other date here is a local-midnight calendar label.
  const todayStart = startOfDay(getTimeWithOffset(timeZone, today));
  const todayKey = toDateKey(todayStart);

  const dates: Date[] = [];
  for (let offset = 0; offset < CAPACITY_WINDOW_DAYS; offset++) {
    dates.push(addDays(firstDay, offset));
  }

  const dayByKey = new Map<string, SuperApiClaimOwnerDayCapacity>();
  (capacity.days ?? []).forEach((day) => {
    const parsed = parseDateKey(day.date);
    if (parsed) dayByKey.set(toDateKey(parsed), day);
  });

  const weekByKey = new Map<string, SuperApiClaimOwnerWeekCapacity>();
  (capacity.weeks ?? []).forEach((week) => {
    const parsed = parseDateKey(week.weekStart);
    if (parsed) weekByKey.set(toDateKey(parsed), week);
  });

  // One entry per Monday-to-Sunday week the seven days touch (AC 5).
  const weeks: CapacityWeek[] = [];
  // Deduped by key: an unserved Monday is never pushed to `weeks`, so the
  // `weeks.some` guard above cannot dedupe it and up to seven days would each
  // count the same absent week.
  const unmatchedWeekKeys = new Set<string>();
  dates.forEach((date) => {
    const start = mondayOf(date);
    const key = toDateKey(start);
    if (weeks.some((week) => week.key === key)) return;

    const served = weekByKey.get(key);
    if (!served) {
      unmatchedWeekKeys.add(key);
      return;
    }

    const end = parseDateKey(served.weekEnd) ?? addDays(start, 6);
    // A week entirely in the past has nothing left to give, whatever the
    // arithmetic says (AC 24).
    const hasFutureDays = end.getTime() >= todayStart.getTime();
    weeks.push({
      key,
      start,
      end,
      cap: served.cap,
      used: served.used,
      remaining: hasFutureDays ? Math.max(0, served.cap - served.used) : 0,
      hasFutureDays,
      containsToday:
        start.getTime() <= todayStart.getTime() &&
        todayStart.getTime() <= end.getTime(),
      // Filled in below: the residual needs the days, and the days need the
      // weeks' `remaining`, so it cannot be computed here.
      usedOutsideWindow: 0,
      usedEarlierThisWeek: 0,
    });
  });

  const weekFor = (date: Date) =>
    weeks.find((week) => week.key === toDateKey(mondayOf(date)));

  const days = dates.map<CapacityDay>((date) => {
    const key = toDateKey(date);
    const week = weekFor(date);
    const base = {
      date,
      key,
      cap: 0,
      used: 0,
      remaining: 0,
      isToday: key === todayKey,
      isPast: date.getTime() < todayStart.getTime(),
      isOff: false,
      isMissing: false,
      isWeeklyGated: false,
      week,
    };

    // Past days never carry figures, in any view range (AC 25).
    if (base.isPast) {
      return { ...base, isOff: true };
    }

    const served = dayByKey.get(key);
    if (!served || !week) {
      // A column with no matching entry is handled, not an error path. The axis
      // and the payload share ONE calendar — the board's selected
      // `scheduleTimeZoneId` — and the contract requires exactly one entry per
      // requested date, so a gap is not a zone artefact. It means the drawn axis
      // has moved ahead of the payload that fed it: this refresh re-renders the
      // last fetched capacity against the CURRENT axis, so a zoom transiently
      // draws days outside the fetched window before the one-week clamp
      // restores the anchored week. NAVIGATION is not the case — changing the
      // range changes the query key and the payload goes away with it, so those
      // rows show the informational note instead.
      //
      // `isOff` is set so every downstream figure stays suppressed, but the
      // RENDERING is deliberately not the unavailability one: AC 31 requires a
      // missing date be surfaced as a data defect and "never rendered as a day
      // the resource is unavailable", so the renderers and the tooltip branch on
      // `isMissing` first and show a subdued dash with no "off". A column is
      // still drawn, so nothing throws, blanks, or shifts the other columns.
      // The systemic cases are caught by the impossible-state reporting below.
      return { ...base, isMissing: true, isOff: true };
    }

    // Zero cap is an em dash, visually distinct from an unused available cap
    // (AC 7).
    //
    // `used` is carried through even though the column cannot display it. `cap`
    // is schedule-reduced on the server while `used` is counted from the audit
    // feed independently, so a day with cap 0 and used 3 is representable — one
    // booked that then acquires an all-day block. Dropping it here would keep
    // that consumption out of the weekly `shown` sum below, pushing it into
    // `usedOutsideWindow`, and because such a day is `isOff` but NOT
    // `isMissing` the unknown-future-day guard would not catch it: a day still
    // in the future would be reported as "used earlier this week". `isOff`
    // governs what the column renders; it must not govern what the week counts.
    if (served.cap === 0) {
      return { ...base, used: served.used, isOff: true };
    }

    // Weekly headroom gates the day. This is the existing enforcement order
    // and the display must not invert it (AC 3).
    const dailyRemaining = Math.max(0, served.cap - served.used);
    const remaining = Math.min(dailyRemaining, week.remaining);
    return {
      ...base,
      cap: served.cap,
      used: served.used,
      remaining,
      isWeeklyGated: remaining === 0 && dailyRemaining > 0,
    };
  });

  /**
   * The elapsed portion of each week (AC 29).
   *
   * A full-week total is surprising mid-week: the week can read "0 remaining"
   * while every visible day looks free, because the capacity went on days the
   * seven-day window does not show. The payload already answers it — no extra
   * request — as the week's total minus the part of it on screen.
   *
   * THE SUM IS RESTRICTED TO THE WEEK'S OWN DAYS. The seven days straddle two
   * Monday-to-Sunday weeks, so summing all of them subtracts the TRAILING
   * week's bookings from the CURRENT week's total and goes negative.
   *
   * These mutate the week objects already pushed above rather than rebuilding
   * them, which is what keeps `days[].week` and `weeks[]` the same objects —
   * one model, so the band and the tooltip cannot drift apart.
   */
  // `dates` is unconditionally CAPACITY_WINDOW_DAYS long, built just above, so
  // both ends exist.
  const windowStart = dates[0];
  const windowEnd = dates[dates.length - 1];
  weeks.forEach((week) => {
    const daysInWeek = days.filter((day) => day.week?.key === week.key);
    const shown = daysInWeek.reduce((sum, day) => sum + day.used, 0);
    week.usedOutsideWindow = Math.max(0, week.used - shown);

    /*
     * The residual is only "earlier this week" when every day it could have
     * come from is already gone. Three sources, and all three must precede
     * today:
     *
     *  - days of the week AFTER the window ends: none of them exist only when
     *    the window reaches the end of the week, and otherwise they are still
     *    to come (the week holds today), so the window must cover `week.end`;
     *  - days of the week BEFORE the window starts: these precede today
     *    exactly when the window itself starts on or before today;
     *  - days inside the window carrying no figures — a past column, which is
     *    before today by definition, or one the payload never served, whose
     *    consumption is simply unknown. Only the unserved kind can sit in the
     *    future, so a non-past missing day makes the attribution a guess.
     *
     * Anything else is suppressed rather than mislabelled: the trailing week's
     * residual is booked AFTER the window, and calling that "earlier this
     * week" would be actively wrong.
     */
    const windowCoversWeekEnd = windowEnd.getTime() >= week.end.getTime();
    const windowStartsByToday = windowStart.getTime() <= todayStart.getTime();
    const hasUnknownFutureDay = daysInWeek.some(
      (day) => day.isMissing && !day.isPast
    );

    week.usedEarlierThisWeek =
      week.containsToday &&
      windowCoversWeekEnd &&
      windowStartsByToday &&
      !hasUnknownFutureDay
        ? week.usedOutsideWindow
        : 0;
  });

  return {
    days,
    weeks,
    gated: days.find((day) => day.isWeeklyGated) ?? null,
    timeZoneId: capacity.timeZoneId ?? null,
    missingDayCount: days.filter((day) => day.isMissing).length,
    unmatchedWeekCount: unmatchedWeekKeys.size,
  };
}

/* -------------------------------------------------------------------------- */
/* Shared fragments                                                           */
/* -------------------------------------------------------------------------- */

/** Availability tone: none / almost gone / fine. */
function remainingTone(remaining: number): 'none' | 'tight' | 'ok' {
  if (remaining === 0) return 'none';
  if (remaining <= 1) return 'tight';
  return 'ok';
}

function dayFigureHtml(day: CapacityDay): string {
  // BEFORE the isOff branch, because a missing day sets both. AC 31: a date the
  // payload never served is a data defect, "never rendered as a day the resource
  // is unavailable" — so it must not reuse the unavailability glyph. Same dash
  // keeps the row aligned; the styling is what separates "no data yet" from
  // "not working".
  if (day.isMissing) {
    return '<span class="capacity-nodata">&mdash;</span>';
  }
  // ONE GLYPH FOR FOUR CAUSES, DELIBERATELY. After the Cap change a zero cap can
  // mean no capacity configured for that weekday, no schedule that day, a
  // blocking event of eight hours or more, or no Location able to serve it. They
  // are not separated because all four say the same thing to a dispatcher — no
  // capacity that day — and no criterion asks them apart. Only "no payload
  // entry" is split out above, because that one does NOT mean that and AC 31
  // requires the distinction. Decided with Justin 2026-08-26 on PR 42552.
  if (day.isOff) {
    return '<span class="capacity-off">&mdash;</span>';
  }
  // Used FIRST, then cap. Flipped from "cap / used" on Justin's call 2026-08-27:
  // consumption is the number a dispatcher scans for, and the weekly row below
  // already reads "used of cap", so the two rows now agree instead of inverting
  // between them.
  return `<span class="capacity-figure">${day.used}<i>/</i>${day.cap}</span>`;
}

function dayRemainingHtml(day: CapacityDay): string {
  // Deliberately NOT "off": that word is the unavailability label, and applying
  // it to a day whose figures simply have not arrived is the AC 31 violation.
  if (day.isMissing) {
    return '<span class="capacity-remaining nodata">&ndash;</span>';
  }
  if (day.isOff) {
    return '<span class="capacity-remaining na">off</span>';
  }
  return `<span class="capacity-remaining ${remainingTone(day.remaining)}">${
    day.remaining
  }</span>`;
}

function weekRangeLabel(week: CapacityWeek): string {
  return `Mon ${format(week.start, SHORT_DATE_FORMAT)} &ndash; Sun ${format(
    week.end,
    SHORT_DATE_FORMAT
  )}`;
}

function weekFiguresHtml(week: CapacityWeek): string {
  // A week wholly behind today reports NOTHING, matching the day rows: a past day is an em dash and
  // "off", so the week those days make up must not read "3 of 10 · 0 remaining" beside them. It used
  // to (AC 24 zeroed `remaining` and left the rest), which put a cap and a usage figure on a week
  // nobody can act on. The straddling case is deliberately untouched -- a week containing today or
  // any day after it keeps its figures wherever it is hovered, because the figure describes the whole
  // Monday-to-Sunday span rather than the column the pointer happens to be over.
  // "Elapsed", not "no capacity", because AC 24 asks this line to state that the week has GONE BY.
  // The distinction is load-bearing now that a field-served resource also reports nothing: that case
  // keeps its figures (cap 0, and whatever was used), so a reader who sees this phrase knows the
  // cause is the calendar rather than the resource's configuration.
  if (!week.hasFutureDays) {
    return '<span class="capacity-week-off">week elapsed</span>';
  }

  const toneCls = week.remaining === 0 ? ' none' : '';
  // AC 29: name the part of the total that was consumed before the window
  // starts, so "0 remaining" beside a row of free-looking days is explained.
  // Suppressed at zero, which is every week whose usage is all on screen —
  // those read exactly as they did before.
  const elapsed = week.usedEarlierThisWeek
    ? ` <span class="capacity-week-elapsed">(${week.usedEarlierThisWeek} used earlier this week)</span>`
    : '';
  return `${week.used} of ${week.cap} &middot; <span class="capacity-week-remaining${toneCls}">${week.remaining} remaining</span>${elapsed}`;
}

/**
 * AC 8: a single informational line, not an error and not a row of zeros.
 *
 * Deliberately cause-NEUTRAL, and MORE so since 2026-08-26. An unresolvable
 * field-location zone is no longer one of the causes — the block is now reported
 * with a null `timeZoneId` instead. What remains is one cause about the resource
 * (no Claim Owner capacity record) and two that are not about it at all: the
 * capacity read degrading, which omits the block for every resource on the page,
 * and the feature being disabled for the tenant. The payload does not say which,
 * so claiming "none configured" would be a confident, wrong statement about
 * configuration.
 */
function noCapacityHtml(): string {
  return (
    '<div class="capacity-note">' +
    '<span class="capacity-note-glyph">i</span>' +
    '<span>Claim Owner capacity cannot be shown for this resource.</span>' +
    '</div>'
  );
}

/**
 * AC 4: the weekly cap being the binding limit has to be stated on screen, not
 * left implicit in a red zero.
 */
function gateNoticeHtml(model: CapacityModel): string {
  const gated = model.gated;
  if (!gated || !gated.week) return '';
  const unused = gated.cap - gated.used;
  return (
    '<div class="capacity-gate">' +
    `Weekly cap reached for ${weekRangeLabel(
      gated.week
    )} &mdash; no capacity ` +
    `remains this week even though ${format(gated.date, 'EEE MM/dd')} has an ` +
    `unused daily cap of ${unused}.` +
    '</div>'
  );
}

/* -------------------------------------------------------------------------- */
/* Renderers                                                                  */
/* -------------------------------------------------------------------------- */

/** One label, in the sticky span that pins it to the region's left edge. */
function labelText(text: string): string {
  return `<span class="capacity-label-text">${text}</span>`;
}

/**
 * Locked-region half: the row labels only.
 *
 * RowExpander calls its renderer once per grid region, which is what makes
 * this design work — the labels sit in the locked region beside the resource
 * columns while the figures are projected onto the time axis.
 *
 * Each label is wrapped in its own span rather than being a bare text child of
 * the row. The span is what holds the left edge when the locked region is
 * narrowed or scrolled: `position: sticky` can only offset a box within its
 * containing block, and the row is as wide as the scrollable content, so
 * sticking the ROW would buy exactly 0px of travel. The span is narrow enough
 * to have somewhere to go. Paired with the region's minimum width in
 * SchedulerCore, this is what stops a closed splitter leaving the figures
 * opposite with nothing naming them.
 */
export function renderCapacityLabels(
  capacity: SuperApiClaimOwnerCapacity | null | undefined
): string {
  if (!capacity) {
    return (
      '<div class="capacity-strip capacity-strip-labels">' +
      `<div class="capacity-label-row">${labelText('Capacity')}</div>` +
      '<div class="capacity-label-row r2"></div>' +
      '<div class="capacity-label-row r3"></div>' +
      '</div>'
    );
  }
  return (
    '<div class="capacity-strip capacity-strip-labels">' +
    `<div class="capacity-label-row">${labelText('Used / cap')}</div>` +
    `<div class="capacity-label-row r2">${labelText('Remaining')}</div>` +
    `<div class="capacity-label-row r3">${labelText('Weekly')}</div>` +
    '</div>'
  );
}

/** The rendered time axis, reduced to what the projection needs. */
export interface CapacityAxis {
  startDate: Date;
  endDate: Date;
  /** Total rendered width of the time axis, in pixels. */
  totalSize: number;
}

/**
 * Linear projection of a timestamp onto the rendered axis.
 *
 * Exact for every preset Scheduler View permits (all uniform) and, unlike
 * `getCoordinateFromDate`, it never returns -1 for an out-of-range date, so
 * the caller clamps instead of branching.
 */
function axisProjection(axis: CapacityAxis) {
  const from = axis.startDate.getTime();
  const to = axis.endDate.getTime();
  const span = to - from;
  const total = axis.totalSize;
  return (ms: number): number => {
    if (!span || !total) return 0;
    return Math.max(0, Math.min(total, ((ms - from) / span) * total));
  };
}

export interface RenderCapacityBandsOptions {
  capacity: SuperApiClaimOwnerCapacity | null | undefined;
  /** First of the seven days — the scheduler's displayed range start. */
  rangeStart: Date;
  axis: CapacityAxis;
  resourceId: string;
  today?: Date;
  /** The board's selected time zone — the zone "today" is resolved in. */
  timeZone?: string;
}

/* -------------------------------------------------------------------------- */
/* Silent-failure detection                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A drawn column whose date key matched nothing renders an em dash and "off" —
 * pixel-identical to a claim owner who genuinely does not work that day. That
 * is deliberate (see the `isMissing` comment) and it is also why a bucketing
 * regression would be invisible: nothing throws and nothing logs.
 *
 * This is REGRESSION INSURANCE, not a bug fix. The bucketing calendar is the
 * request's `scheduleTimeZoneId` and is pinned by tests, so no mismatch is
 * expected today.
 */
export const CAPACITY_MISMATCH_EVENT = 'CapacityBandCalendarMismatch';

/** Reported `resourceId|reason` pairs — see the repeat-guard note below. */
const reportedMismatches = new Set<string>();

/**
 * Forget every reported mismatch. Test seam only: the guard is process-lifetime
 * by design, so a suite asserting the first emission needs it cleared.
 */
export function resetCapacityMismatchReports(): void {
  reportedMismatches.clear();
}

/**
 * Report a calendar mismatch that CANNOT legitimately occur — and only that.
 *
 * An individual missing day is not worth reporting: this refresh re-renders the
 * last fetched capacity against the CURRENT axis, so a zoom transiently draws
 * days outside the fetched window before the one-week clamp restores the
 * anchored week, and a healthy resource shows gaps for a frame. Reporting per
 * miss would fire repeatedly through one zoom gesture and be worthless. (It is
 * NOT that the axis and the payload use different calendars — they share the
 * board's selected `scheduleTimeZoneId`, and the contract requires one entry per
 * requested date.) Only two states are impossible:
 *
 *  - the API served weeks and not one matched a computed Monday, which empties
 *    `model.weeks` AND strips all seven days — a whole week gone, looking like
 *    a claim owner who is off all week;
 *  - the API served days and every column that could have carried figures
 *    missed. Past columns are excluded because they never match by design.
 *
 * The two overlap by construction (`!week` also flags every day missing), so
 * the week reason wins — it is the cause, the day misses are its symptom.
 *
 * NOT REPORTED, AND KNOWN: a PARTIAL week miss. If the API serves one of the two
 * Monday-to-Sunday weeks the window touches, neither reason fires —
 * `model.weeks.length` is 1, and the served week's own days match, so
 * `missingDayCount` stays below `candidateDays`. The unserved week's dates
 * render as an em dash and "off", indistinguishable from days a claim owner does
 * not work, for up to six columns. `model.unmatchedWeekCount` already measures
 * it and is already on the event payload, so a third reason keyed on
 * `servedWeeks > 0 && unmatchedWeekCount > 0` would close it with no new
 * plumbing.
 *
 * It is left open deliberately rather than by oversight, because it is a
 * signal-to-noise decision and not a free one: unlike navigation — which changes
 * the query key and takes the payload with it — a ZOOM keeps the payload while
 * the axis drifts, so the drawn week can transiently be one the payload never
 * served. A third reason would fire on that, once per resource, on a board that
 * is working correctly. Whoever decides to add it should decide that too, and
 * this note exists so the next reader does not assume the counter already covers
 * a partial miss.
 *
 * REPEAT GUARD. `refreshCapacityBands` re-renders on every `timeAxisChange`,
 * which fires repeatedly through a single zoom gesture. Without this, one zoom
 * emits dozens of identical events. Keyed per resource AND reason so the two
 * distinct signals are not collapsed into one.
 *
 * PRIVACY. Identifiers, counts, window dates, and the board's selected time
 * zone — a toolbar setting, no more identifying than a page size. Nothing here
 * may carry a resource or adjuster name, a claim number, an insured's details,
 * or any location: the surrounding scheduler payload has several such fields
 * and none belong in telemetry. The spec asserts the property key set EXACTLY,
 * so a future addition fails the build rather than leaking quietly.
 */
function reportCapacityMismatch(
  capacity: SuperApiClaimOwnerCapacity | null | undefined,
  model: CapacityModel,
  resourceId: string,
  timeZone: string | undefined
): void {
  const servedDays = (capacity?.days ?? []).length;
  const servedWeeks = (capacity?.weeks ?? []).length;
  // Past days never carry figures and never match, so they are not candidates.
  //
  // These count MODEL days, not the columns actually appended to the DOM: the
  // render loop below skips a column narrower than 1px, so the two are equal
  // only while `minZoomLevel: 13` holds (256–1680px/day). Loosen that pin and
  // this needs a real drawn-count threaded through.
  const candidateDays = model.days.filter((day) => !day.isPast).length;

  let reason: string;
  if (servedWeeks > 0 && model.weeks.length === 0) {
    reason = 'weeks-unmatched';
  } else if (
    servedDays > 0 &&
    candidateDays > 0 &&
    model.missingDayCount === candidateDays
  ) {
    reason = 'days-unmatched';
  } else {
    return;
  }

  const key = `${resourceId}|${reason}`;
  if (reportedMismatches.has(key)) return;
  reportedMismatches.add(key);

  const properties: Record<string, string> = {
    reason,
    resourceId,
    windowStart: model.days[0]?.key ?? '',
    windowEnd: model.days[model.days.length - 1]?.key ?? '',
  };
  // The zone the band is BUCKETED in — the board's selected
  // `scheduleTimeZoneId`, the same value `isToday`/`isPast` resolve against.
  // Without it you know a calendar mismatch happened but not which calendar
  // was involved, which is most of the diagnostic value.
  //
  // Deliberately NOT `capacity.timeZoneId`: that is the resource's
  // field-location zone, it describes where a person works, and it no longer
  // governs bucketing. Omitted rather than defaulted when absent, so an
  // unset zone never reaches telemetry as the literal string "undefined".
  if (timeZone) properties.timeZoneId = timeZone;

  trackEvent(CAPACITY_MISMATCH_EVENT, properties, {
    servedDays,
    servedWeeks,
    drawnDays: model.days.length,
    candidateDays,
    missingDayCount: model.missingDayCount,
    unmatchedWeekCount: model.unmatchedWeekCount,
  });
}

/**
 * Time-axis half: per-day figures and the weekly bars, positioned by pixel.
 *
 * Cells carry the resource id and date so the hover tooltip can resolve them
 * from the same model later, without a second copy of the arithmetic.
 */
export function renderCapacityBands({
  capacity,
  rangeStart,
  axis,
  resourceId,
  today,
  timeZone,
}: RenderCapacityBandsOptions): string {
  const model = buildCapacityModel(capacity, rangeStart, today, timeZone);
  if (!model) {
    return `<div class="capacity-strip">${noCapacityHtml()}</div>`;
  }

  // Detection only. Fired from the render boundary rather than the model
  // builder so `buildCapacityModel` stays pure, and it changes nothing that is
  // drawn below — the em dash stays exactly as it is.
  reportCapacityMismatch(capacity, model, resourceId, timeZone);

  const project = axisProjection(axis);
  const res = escapeHtml(resourceId);

  let capRow = '';
  let remainingRow = '';
  let weekRow = '';

  model.days.forEach((day) => {
    // addDays, not +86400000: on a DST boundary a calendar day is 23 or 25
    // hours long and a fixed millisecond step slides the cell off its column.
    const left = project(day.date.getTime());
    const right = project(addDays(day.date, 1).getTime());
    if (right - left < 1) return;

    const style = `left:${left}px;width:${right - left}px`;
    const cls = [
      'capacity-cell',
      day.isToday ? 'is-today' : '',
      day.isPast ? 'is-past' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const tags = ` data-capacity-resource="${res}" data-capacity-date="${day.key}"`;

    // A missing day is not special-cased here: `isOff` already carries it, so
    // it draws the same em dash and "off" a zero-cap day does rather than
    // leaving a hole in the row.
    capRow += `<div class="${cls}" style="${style}"${tags}>${dayFigureHtml(
      day
    )}</div>`;
    remainingRow += `<div class="${cls}" style="${style}"${tags}>${dayRemainingHtml(
      day
    )}</div>`;
  });

  // Each weekly bar spans only its own days within the seven on screen (AC 5).
  model.weeks.forEach((week, index) => {
    const daysInWeek = model.days.filter((day) => day.week?.key === week.key);
    if (!daysInWeek.length) return;

    const left = project(daysInWeek[0].date.getTime());
    const right = project(
      addDays(daysInWeek[daysInWeek.length - 1].date, 1).getTime()
    );
    if (right - left < 2) return;

    const narrow = right - left < WEEK_RANGE_MIN_WIDTH_PX ? ' is-narrow' : '';

    // Tagged with the resource and the week's Monday so the hover can resolve this bar the same way
    // it resolves a day cell. Without these the weekly row fell through to Bryntum's own date
    // readout, which is how a bar whose label had scrolled out of view became unreadable: the label
    // sits at the bar's left edge, a week is up to seven columns wide, and the axis is far wider than
    // the viewport, so scrolling into the middle of a week left the figures thousands of pixels off
    // to the left with no way to ask for them.
    weekRow +=
      `<div class="capacity-week ${index === 0 ? 'a' : 'b'}${narrow}" ` +
      `style="left:${left}px;width:${right - left}px"` +
      ` data-capacity-resource="${res}" data-capacity-week="${week.key}">` +
      `<span class="capacity-week-range">${weekRangeLabel(week)}</span>` +
      `<span class="capacity-week-figures">${weekFiguresHtml(week)}</span>` +
      '</div>';
  });

  return (
    '<div class="capacity-strip capacity-strip-bands">' +
    `<div class="capacity-band r1">${capRow}</div>` +
    `<div class="capacity-band r2">${remainingRow}</div>` +
    `<div class="capacity-band r3">${weekRow}</div>` +
    '</div>' +
    gateNoticeHtml(model)
  );
}

/* -------------------------------------------------------------------------- */
/* Bryntum glue                                                               */
/* -------------------------------------------------------------------------- */

/** The Bryntum locked region, where the labels go. */
export const CAPACITY_LOCKED_REGION = 'locked';

/**
 * Narrowest the locked region may get while capacity is shown, in pixels.
 *
 * Left-aligning and pinning the labels is not enough on its own: the splitter
 * can be dragged until the locked region is ~0px, and at that point everything
 * inside it is clipped no matter how it is aligned, while the time-axis half
 * keeps drawing figures. Bryntum's RegionResize clamps every drag move to
 * `subGrid.minWidth`, so this is the floor that keeps the labels reachable.
 *
 * Sized from the widest label as actually rendered, not from arithmetic:
 * measured at **84.3px** including the span's 10px gutters, against real
 * Bryntum 7.2.0 at this rule's own 10px/600/uppercase/0.06em. "Used / cap" is
 * the widest; "Remaining" is a character shorter. 96 leaves ~12px of headroom
 * for font fallback. It is NOT sized to the columns — Resource/Team/County are
 * meant to scroll out of reach at the floor.
 *
 * This deliberately does NOT block the splitter's collapse chevron, which
 * calls `subGrid.collapse()` and bypasses `minWidth` entirely. Collapsing that
 * way is explicit and one click to undo; the accidental drag is what this
 * guards.
 */
export const CAPACITY_LOCKED_MIN_WIDTH = 96;

/** Marks a wrapper so both halves can be found and refreshed in place. */
export const CAPACITY_HOST_ATTR = 'data-capacity-host';
/** Records which region a wrapper belongs to. */
export const CAPACITY_REGION_ATTR = 'data-capacity-region';

/**
 * Read the capacity payload off a resource — either the raw API object or the
 * Bryntum record wrapping it.
 *
 * `claimOwnerCapacity` is the field name the API DTO uses, so it is also the
 * property on `SuperApiResource`. Reads through `data` as well as the object
 * itself because the resource store runs with `useRawData: true`, so raw API
 * fields can appear either way.
 *
 * Returns null ONLY when there is no capacity block — that is the sole signal
 * the API gives for "nothing to report". It has three causes: the resource has
 * no Claim Owner capacity record, the capacity read degraded (which omits the
 * block for every resource on the page rather than reporting a confident zero),
 * or the feature is disabled for the tenant. Only the first says anything about
 * the resource, and the band cannot tell them apart, which is why the AC 8
 * message is cause-neutral. An unresolvable field-location zone is NOT among
 * them since 2026-08-26: the block is reported with a null `timeZoneId`.
 */
export function readClaimOwnerCapacity(
  record: unknown
): SuperApiClaimOwnerCapacity | null {
  if (!record || typeof record !== 'object') return null;
  const holder = record as {
    claimOwnerCapacity?: unknown;
    data?: { claimOwnerCapacity?: unknown };
  };
  const raw = holder.claimOwnerCapacity ?? holder.data?.claimOwnerCapacity;
  if (!raw || typeof raw !== 'object') return null;

  const candidate = raw as Partial<SuperApiClaimOwnerCapacity>;
  // A PRESENT block is configured capacity, whatever its arrays hold. `days`
  // and `weeks` are always serialized and an EMPTY list means "the window
  // produced no days", not "not configured" — only a null or absent block
  // signals that, and only it may produce the AC 8 message. Returning null for
  // an empty `days` here would put the message on a configured resource.
  return {
    timeZoneId: candidate.timeZoneId ?? null,
    weeklyCap: candidate.weeklyCap ?? 0,
    days: Array.isArray(candidate.days) ? candidate.days : [],
    weeks: Array.isArray(candidate.weeks) ? candidate.weeks : [],
  };
}

/** Minimum surface of the scheduler the band needs. */
export interface CapacityScheduler {
  timeAxis?: { startDate?: Date; endDate?: Date };
  timeAxisViewModel?: { totalSize?: number };
  element?: HTMLElement | null;
}

/**
 * Read the rendered axis geometry, or `null` before the first paint.
 */
export function capacityAxisFrom(
  scheduler: CapacityScheduler | null | undefined
): CapacityAxis | null {
  const startDate = scheduler?.timeAxis?.startDate;
  const endDate = scheduler?.timeAxis?.endDate;
  const totalSize = scheduler?.timeAxisViewModel?.totalSize;
  if (!startDate || !endDate || !totalSize) return null;
  return { startDate, endDate, totalSize };
}

/** Minimum surface needed to floor the locked region. */
export interface CapacityRegionHost {
  subGrids?: Record<string, { minWidth?: number } | undefined>;
}

/**
 * Raise or lift the locked region's width floor as the capacity toggle flips.
 *
 * Deliberately NOT a `subGridConfigs` entry. A static floor would apply to
 * every board, including a tenant whose capacity flag is off — and that would
 * break the promise SchedulerPage makes, that a flag-off board is identical to
 * today's. Setting it on the live subgrid keeps the floor as short-lived as the
 * thing that needs it.
 *
 * Returns whether the floor was actually applied, so a caller running before
 * Bryntum has built its subgrids can tell "not ready" from "done".
 */
export function applyCapacityRegionFloor(
  scheduler: CapacityRegionHost | null | undefined,
  isCapacityShown: boolean
): boolean {
  const locked = scheduler?.subGrids?.[CAPACITY_LOCKED_REGION];
  if (!locked) return false;

  // 0 rather than `undefined`: RegionResize reads `subGrid.minWidth || 0` when
  // it starts a drag, so both mean "no floor" to Bryntum — but assigning a
  // number keeps the property's type stable for anything else reading it.
  locked.minWidth = isCapacityShown ? CAPACITY_LOCKED_MIN_WIDTH : 0;
  return true;
}

/**
 * Content for one expanded row, one region.
 *
 * Wrapped in a host tagged with the record id AND the region so a range or
 * zoom change can rewrite either half IN PLACE. That matters: RowExpander
 * caches its body elements per record, so collapsing and re-expanding can
 * reuse the previous DOM instead of re-invoking this renderer.
 *
 * `width: 100%` on the host is load-bearing and lives in the stylesheet:
 * `.b-row-expander-body` is a flex container, so a host left to size itself
 * shrinks to its content — and the band's content is absolutely positioned,
 * meaning zero intrinsic width, so the strip's background never paints at all.
 */
export function renderCapacityHost(
  resourceId: string,
  region: string,
  capacity: SuperApiClaimOwnerCapacity | null,
  axis: CapacityAxis | null,
  today?: Date,
  timeZone?: string
): string {
  const res = escapeHtml(resourceId);
  const attrs =
    `class="capacity-host" ${CAPACITY_HOST_ATTR}="${res}" ` +
    `${CAPACITY_REGION_ATTR}="${escapeHtml(region)}"`;

  if (region === CAPACITY_LOCKED_REGION) {
    return `<div ${attrs}>${renderCapacityLabels(capacity)}</div>`;
  }

  // Without axis geometry there is nothing to project onto yet; the wrapper is
  // still emitted so the next refresh can fill it.
  const inner = axis
    ? renderCapacityBands({
        capacity,
        rangeStart: axis.startDate,
        axis,
        resourceId,
        today,
        timeZone,
      })
    : '';
  return `<div ${attrs}>${inner}</div>`;
}

/**
 * Rewrite every mounted band in place after the axis moves.
 *
 * Design A positions content in absolute pixels, so it goes stale on every
 * zoom and shift. Refreshing in place (rather than collapsing and re-expanding)
 * is what keeps AC 11 true: same figures, no duplicated rows, no extra request.
 *
 * ONE PASS COSTS THE WHOLE BOARD, so callers must not call it once per row.
 * SchedulerCore wraps it in `coalesceFrame`, which collapses a burst of
 * triggers (a bulk expand's N × `rowExpand`, a zoom's repeated
 * `timeAxisChange`) into a single pass. This function stays synchronous and
 * un-throttled on purpose: it reads the axis and capacity at call time, so the
 * coalesced pass always renders the burst's FINAL state.
 */
export function refreshCapacityBands(
  scheduler: CapacityScheduler | null | undefined,
  getCapacity: (resourceId: string) => SuperApiClaimOwnerCapacity | null,
  today?: Date,
  timeZone?: string
): void {
  const root = scheduler?.element;
  const axis = capacityAxisFrom(scheduler);
  if (!root || !axis) return;

  const hosts = root.querySelectorAll<HTMLElement>(`[${CAPACITY_HOST_ATTR}]`);
  hosts.forEach((host) => {
    const resourceId = host.getAttribute(CAPACITY_HOST_ATTR);
    if (!resourceId) return;
    if (host.getAttribute(CAPACITY_REGION_ATTR) === CAPACITY_LOCKED_REGION) {
      // Labels are static; leave the locked half alone so a refresh cannot
      // flicker it.
      return;
    }
    host.innerHTML = renderCapacityBands({
      capacity: getCapacity(resourceId),
      rangeStart: axis.startDate,
      axis,
      resourceId,
      today,
      timeZone,
    });
  });
}
